import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Loader2 } from "lucide-react";

// Guardia unica attiva (scheda o finestra aperta per ultima) per le uscite con modifiche non salvate.
let active = null;
// Voce di cronologia "sentinella" (stesso URL) che permette di intercettare il tasto Indietro del browser.
let dummyUrl = null;
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    if (!active && dummyUrl && window.location.href === dummyUrl && !window.history.state?.__guard) { dummyUrl = null; window.history.back(); }
  });
}
export const richiediUscita = (proceed) => (active ? active.ask(proceed) : proceed());
export const salvatoOk = () => setTimeout(() => {
  const altri = (toast.getToasts?.() || []).some((t) => t.type === "success" && t.id !== "salvato" && !t.dismiss);
  if (!altri) toast.success("Salvato", { id: "salvato", duration: 3000, style: { background: "#059669", color: "#fff", border: "1px solid #047857" } });
}, 400);

export function useUnsavedGuard(dirty, save) {
  const nav = useNavigate();
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const saveRef = useRef(save);
  saveRef.current = save;
  const bypass = useRef(false);
  const ask = (proceed) => setPending(() => proceed);

  useEffect(() => {
    if (!dirty) return undefined;
    bypass.current = false;
    const me = { ask }; const prev = active; active = me;
    const onKey = (e) => { if (active === me && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); saveRef.current(); } };
    const onUnload = (e) => { if (bypass.current) return; e.preventDefault(); e.returnValue = ""; };
    const onClick = (e) => {
      if (active !== me || bypass.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname + url.search === window.location.pathname + window.location.search) return;
      e.preventDefault(); e.stopPropagation();
      ask(() => nav(url.pathname + url.search + url.hash));
    };
    const onPop = () => {
      if (active !== me || bypass.current) return;
      window.history.pushState({ ...(window.history.state || {}), __guard: true }, "");
      ask(() => { bypass.current = true; dummyUrl = null; window.history.go(-2); });
    };
    if (!window.history.state?.__guard) { window.history.pushState({ ...(window.history.state || {}), __guard: true }, ""); }
    dummyUrl = window.location.href;
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("beforeunload", onUnload);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPop);
      if (active === me) active = prev;
    };
  }, [dirty]); // eslint-disable-line

  const esci = (p) => { bypass.current = true; setPending(null); p(); };
  const salvaEsci = async () => {
    const p = pending; setBusy(true);
    const ok = await saveRef.current();
    setBusy(false);
    if (ok) esci(p); else setPending(null);
  };
  const dialog = pending ? (
    <UscitaDialog busy={busy} onSave={salvaEsci} onExit={() => esci(pending)} onStay={() => setPending(null)} />
  ) : null;
  return { ask, dialog };
}

function UscitaDialog({ busy, onSave, onExit, onStay }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="unsaved-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onStay} />
      <div className="relative w-full max-w-sm card-immo p-6">
        <h3 className="font-display text-lg font-bold">Hai modifiche non salvate</h3>
        <p className="text-sm text-[var(--muted)] mt-1 mb-5">Se esci senza salvare, le modifiche andranno perse.</p>
        <div className="flex flex-col gap-2">
          <button data-testid="unsaved-dialog-save" onClick={onSave} disabled={busy} className="py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy && <Loader2 size={15} className="animate-spin" />} Salva ed esci
          </button>
          <button data-testid="unsaved-dialog-exit" onClick={onExit} disabled={busy} className="py-2.5 rounded-xl bg-white border border-red-200 text-red-600 font-semibold text-sm hover:bg-red-50">Esci senza salvare</button>
          <button data-testid="unsaved-dialog-stay" onClick={onStay} disabled={busy} className="py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Resta</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function UnsavedActions({ saving, error, onSave, onCancel, saveLabel = "Salva" }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 w-full">
      <div className="flex-1 min-w-0 text-sm">
        {error
          ? <span data-testid="unsaved-error" className="flex items-start gap-1.5 text-red-600 font-semibold"><AlertCircle size={16} className="shrink-0 mt-0.5" /><span className="line-clamp-2">{error}</span></span>
          : <span data-testid="unsaved-label" className="flex items-center gap-2 font-semibold text-[#111111]"><span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />Modifiche non salvate</span>}
      </div>
      <button data-testid="unsaved-cancel" onClick={onCancel} disabled={saving} className="px-4 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100 disabled:opacity-60">Annulla</button>
      <button data-testid="unsaved-save" onClick={onSave} disabled={saving} className="px-5 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink disabled:opacity-60 inline-flex items-center gap-2">
        {saving && <Loader2 size={15} className="animate-spin" />}{saveLabel}
      </button>
    </div>
  );
}

export function UnsavedBar({ dirty, ...props }) {
  useEffect(() => {
    if (!dirty) return undefined;
    document.body.classList.add("has-unsaved-bar");
    return () => document.body.classList.remove("has-unsaved-bar");
  }, [dirty]);
  if (!dirty) return null;
  return createPortal(
    <div data-testid="unsaved-bar" className="fixed inset-x-0 bottom-0 z-[45] border-t border-[var(--border)] bg-white/95 backdrop-blur-md shadow-[0_-8px_24px_rgba(0,0,0,0.08)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="max-w-4xl mx-auto lg:pl-64"><UnsavedActions {...props} /></div>
    </div>,
    document.body
  );
}
